// The core stats aggregation, mirroring Android's WastedTimeByDayFlow /
// WastedTimeBreakdownFlow. Dependency-free so it matches the app exactly.
//
// Split into statsFormat (pure formatters/range helpers) and statsTypes (shared
// shapes); this module owns the heavy aggregation. The formatters are
// re-exported below so existing `import { ... } from "../utils/statsCompute"`
// call sites keep working unchanged.

import { startOfDay, dayKey, costFor, hoursOf } from "./statsFormat";
import {
  ComputeStatsInput,
  ComputeStatsResult,
  Task,
  BreakdownRow,
  BreakdownDay,
  DayTotal,
  PdfRow,
} from "./statsTypes";

// Preserve the public surface of the old single-file module.
export * from "./statsFormat";
export * from "./statsTypes";

/**
 * The core aggregation. Returns:
 *   dayTotals  — one entry per day across [startDate, endDate] (incl. empty days),
 *                oldest first: { date, key, totalMs, humanMs, aiMs }
 *   breakdown  — only days with sessions, newest first:
 *                { date, key, totalMs, rows: [BreakdownRow] }
 *   pdfRows    — one row per task (or two when human+AI split is enabled and both
 *                exist): { taskTitle, description, hours, amount, kindLabel }
 *   todayMs, rangeTotalMs, rangeValue
 */
export function computeStats({
  records = [],
  tasksById = new Map(),
  projectsById = new Map(),
  actionLabelById = {},
  startDate,
  endDate,
  projectIds = null, // null = all projects; Set of numeric projectIds otherwise
  labelIds = new Set(),
  hourlyRate = 0,
  aiHourlyRate = 0,
  aiInvoiceEnabled = true,
}: ComputeStatsInput): ComputeStatsResult {
  const startMs = startOfDay(startDate).getTime();
  const endMs = startOfDay(endDate).getTime() + 24 * 3_600_000 - 1; // inclusive end day
  const todayKey = dayKey(startOfDay(new Date()));

  // Materialize the label Set to an array once so the per-task filter below
  // iterates a plain array (es5-safe, no Set iteration in a hot loop).
  const labelIdArr = Array.from(labelIds);
  const passesFilters = (task?: Task): boolean => {
    if (!task) return false;
    if (projectIds && !projectIds.has(Number(task.projectId))) return false;
    if (labelIdArr.length > 0) {
      const ids = new Set((task.labelIds || []).map(Number));
      for (const l of labelIdArr) if (!ids.has(Number(l))) return false;
    }
    return true;
  };

  // ── group by day ──────────────────────────────────────────────────────────
  type DayBucket = { date: Date; humanMs: number; aiMs: number; rows: BreakdownRow[] };
  type TaskBucket = { humanMs: number; aiMs: number; task: Task };
  const byDay = new Map<string, DayBucket>();
  const byTask = new Map<number, TaskBucket>();

  for (const rec of records) {
    const started = Number(rec.startedTime) || 0;
    if (started < startMs || started > endMs) continue;
    const task = tasksById.get(Number(rec.taskId));
    if (!passesFilters(task)) continue;
    const duration = Number(rec.spentTime) || 0;
    if (duration <= 0) continue;
    const isAi = rec.trackerKind === "ai";
    const d = startOfDay(new Date(started));
    const key = dayKey(d);

    let bucket = byDay.get(key);
    if (!bucket) {
      bucket = { date: d, humanMs: 0, aiMs: 0, rows: [] };
      byDay.set(key, bucket);
    }
    if (isAi) bucket.aiMs += duration;
    else bucket.humanMs += duration;

    const project = projectsById.get(Number(task.projectId));
    bucket.rows.push({
      timeRecordId: rec.id,
      taskId: Number(rec.taskId),
      taskTitle: task.title || "",
      projectId: Number(task.projectId),
      projectName: project ? project.projectName || "" : "",
      startedTime: started,
      durationMs: duration,
      trackerKind: isAi ? "ai" : "human",
      actionLabel: rec.actionId != null ? actionLabelById[String(rec.actionId)] || null : null,
    });

    let t = byTask.get(Number(rec.taskId));
    if (!t) {
      t = { humanMs: 0, aiMs: 0, task };
      byTask.set(Number(rec.taskId), t);
    }
    if (isAi) t.aiMs += duration;
    else t.humanMs += duration;
  }

  // ── dayTotals across the full range (incl. empty days), oldest first ───────
  const dayTotals: DayTotal[] = [];
  const cursor = startOfDay(startDate);
  const last = startOfDay(endDate);
  while (cursor <= last) {
    const key = dayKey(cursor);
    const b = byDay.get(key);
    dayTotals.push({
      date: new Date(cursor),
      key,
      humanMs: b ? b.humanMs : 0,
      aiMs: b ? b.aiMs : 0,
      totalMs: b ? b.humanMs + b.aiMs : 0,
    });
    cursor.setDate(cursor.getDate() + 1);
  }

  // ── breakdown: days with sessions, newest first; rows newest first ─────────
  const breakdown: BreakdownDay[] = Array.from(byDay.values())
    .map((b) => ({
      date: b.date,
      key: dayKey(b.date),
      totalMs: b.humanMs + b.aiMs,
      humanMs: b.humanMs,
      aiMs: b.aiMs,
      rows: [...b.rows].sort((a, c) => c.startedTime - a.startedTime),
    }))
    .sort((a, c) => c.date.getTime() - a.date.getTime());

  // ── summary stats ──────────────────────────────────────────────────────────
  const todayMs = dayTotals.find((d) => d.key === todayKey)?.totalMs || 0;
  const rangeTotalMs = dayTotals.reduce((s, d) => s + d.totalMs, 0);
  let rangeValue = 0;
  for (const b of Array.from(byDay.values())) {
    rangeValue += costFor(b.humanMs, hourlyRate) + costFor(b.aiMs, aiHourlyRate);
  }

  // ── PDF rows (per task, human/AI split like Android) ───────────────────────
  const pdfRows: PdfRow[] = [];
  for (const { humanMs, aiMs, task } of Array.from(byTask.values())) {
    const title = (task.title || "").trim() || "(no title)";
    const desc = task.description || "";
    if (!aiInvoiceEnabled) {
      const ms = humanMs + aiMs;
      if (ms <= 0) continue;
      pdfRows.push({ taskTitle: title, description: desc, hours: hoursOf(ms), amount: costFor(ms, hourlyRate), kindLabel: "" });
      continue;
    }
    if (humanMs > 0) {
      pdfRows.push({
        taskTitle: title, description: desc, hours: hoursOf(humanMs),
        amount: costFor(humanMs, hourlyRate), kindLabel: aiMs > 0 ? "Human" : "",
      });
    }
    if (aiMs > 0) {
      pdfRows.push({
        taskTitle: title, description: desc, hours: hoursOf(aiMs),
        amount: costFor(aiMs, aiHourlyRate), kindLabel: "AI",
      });
    }
  }
  pdfRows.sort((a, b) => a.taskTitle.localeCompare(b.taskTitle));

  return { dayTotals, breakdown, pdfRows, todayMs, rangeTotalMs, rangeValue };
}
